import type {
    SeasonalFXConfig,
    ISeasonalFX,
    Season,
    SeasonConfig,
    PerformanceMetrics,
    ParticleCustomization,
} from './types';
import {
    detectSeason,
    prefersReducedMotion,
    isMobileDevice,
    createCanvas,
    resizeCanvas,
    FPSThrottler,
} from './utils';
import { createSeasonEffect } from './effects';
import { ParticleEffect } from './effects/ParticleEffect';

/**
 * Internal resolved configuration with HTMLElement target
 */
type ResolvedSeasonalFXConfig = Required<Omit<SeasonalFXConfig, 'target'>> & {
    target: HTMLElement;
};

/**
 * Main SeasonalFX class
 */
export class SeasonalFX implements ISeasonalFX {
    private config: ResolvedSeasonalFXConfig;
    private canvas: HTMLCanvasElement;
    private ctx: CanvasRenderingContext2D;
    private currentEffect: ParticleEffect | null = null;
    private animationFrameId: number | null = null;
    private running = false;
    private paused = false;
    private fpsThrottler: FPSThrottler;
    private lastFrameTime = 0;
    private currentSeason: Season;
    private resizeObserver: ResizeObserver | null = null;

    // Performance tracking
    private frameCount = 0;
    private fpsUpdateTime = 0;
    private currentFPS = 0;
    private renderTime = 0;

    constructor(config: SeasonalFXConfig) {
        // Validate environment
        if (typeof window === 'undefined') {
            throw new Error('SeasonalFX requires a browser environment');
        }

        if (!config.target) {
            throw new Error('SeasonalFX requires a target element');
        }

        // Resolve target element
        let targetElement: HTMLElement;
        if (typeof config.target === 'string') {
            const element = document.getElementById(config.target) || document.querySelector(config.target);
            if (!element) {
                throw new Error(`Target element "${config.target}" not found`);
            }
            targetElement = element as HTMLElement;
        } else {
            targetElement = config.target;
        }

        // Set defaults
        this.config = {
            target: targetElement,
            autoSeason: config.autoSeason ?? true,
            season: config.season ?? 'winter',
            seasonConfig: config.seasonConfig ?? {},
            intensity: config.intensity ?? 'subtle',
            maxFPS: config.maxFPS ?? 30,
            disableOnMobile: config.disableOnMobile ?? true,
            respectReducedMotion: config.respectReducedMotion ?? true,
            regionalConfig: config.regionalConfig ?? {
                spring: [[3, 5]],
                summer: [[6, 8]],
                autumn: [[9, 11]],
                winter: [[12, 2]],
            },
            debug: config.debug ?? false,
            particleCustomization: config.particleCustomization ?? {},
        };

        // Check if we should disable
        if (this.shouldDisable()) {
            this.log('Effects disabled due to user preferences or device type');
            // Create a no-op instance
            this.canvas = document.createElement('canvas');
            this.ctx = this.canvas.getContext('2d')!;
            this.fpsThrottler = new FPSThrottler(this.config.maxFPS);
            this.currentSeason = 'winter';
            return;
        }

        // Create canvas
        this.canvas = createCanvas(targetElement);
        const context = this.canvas.getContext('2d');

        if (!context) {
            throw new Error('Failed to get canvas 2D context');
        }

        this.ctx = context;
        this.fpsThrottler = new FPSThrottler(this.config.maxFPS);

        // Determine initial season
        this.currentSeason = this.determineSeason();

        // Initialize effect
        this.initializeEffect();

        // Setup resize observer
        this.setupResizeObserver();
    }

    private shouldDisable(): boolean {
        if (this.config.respectReducedMotion && prefersReducedMotion()) {
            return true;
        }

        if (this.config.disableOnMobile && isMobileDevice()) {
            return true;
        }

        return false;
    }

    private determineSeason(): Season {
        if (this.config.season) {
            return this.config.season;
        }

        if (this.config.autoSeason) {
            return detectSeason(new Date(), this.config.regionalConfig);
        }

        return 'winter'; // Default
    }

    private initializeEffect(): void {
        const seasonConfig = this.config.seasonConfig[this.currentSeason];
        const intensity = seasonConfig?.intensity ?? this.config.intensity;
        const variant = seasonConfig?.variant;

        this.currentEffect = createSeasonEffect(
            this.currentSeason,
            intensity,
            variant,
            this.config.particleCustomization
        );

        // Resize canvas and initialize particles
        resizeCanvas(this.canvas, this.config.target);
        this.currentEffect.init(this.canvas.width, this.canvas.height);
    }

    private setupResizeObserver(): void {
        if (typeof ResizeObserver !== 'undefined') {
            this.resizeObserver = new ResizeObserver(() => {
                resizeCanvas(this.canvas, this.config.target);
                if (this.currentEffect) {
                    this.currentEffect.init(this.canvas.width, this.canvas.height);
                }
            });

            this.resizeObserver.observe(this.config.target);
        }
    }

    private animate = (timestamp: number): void => {
        if (!this.running || this.paused) return;

        // FPS throttling
        if (!this.fpsThrottler.shouldRender(timestamp)) {
            this.animationFrameId = requestAnimationFrame(this.animate);
            return;
        }

        const startTime = performance.now();

        // Calculate delta time
        const deltaTime = this.lastFrameTime ? timestamp - this.lastFrameTime : 0;
        this.lastFrameTime = timestamp;

        // Clear canvas
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);

        // Update and render
        if (this.currentEffect) {
            this.currentEffect.update(deltaTime, this.canvas.width, this.canvas.height);
            this.currentEffect.render(this.ctx);
        }

        // Track performance
        this.renderTime = performance.now() - startTime;
        this.updateFPS(timestamp);

        this.animationFrameId = requestAnimationFrame(this.animate);
    };

    private updateFPS(timestamp: number): void {
        this.frameCount++;

        if (timestamp - this.fpsUpdateTime >= 1000) {
            this.currentFPS = Math.round((this.frameCount * 1000) / (timestamp - this.fpsUpdateTime));
            this.frameCount = 0;
            this.fpsUpdateTime = timestamp;

            if (this.config.debug) {
                this.log(`FPS: ${this.currentFPS}, Render time: ${this.renderTime.toFixed(2)}ms`);
            }
        }
    }

    private log(message: string): void {
        if (this.config.debug) {
            console.log(`[SeasonalFX] ${message}`);
        }
    }

    // Public API

    start(): void {
        if (this.shouldDisable()) {
            this.log('Cannot start - effects are disabled');
            return;
        }

        if (this.running) {
            this.log('Already running');
            return;
        }

        this.running = true;
        this.paused = false;

        // Add canvas to DOM
        if (!this.canvas.parentElement) {
            this.config.target.appendChild(this.canvas);
        }

        this.log(`Starting ${this.currentSeason} effect`);
        this.animationFrameId = requestAnimationFrame(this.animate);
    }

    pause(): void {
        if (!this.running) return;

        this.paused = true;
        this.log('Paused');
    }

    resume(): void {
        if (!this.running || !this.paused) return;

        this.paused = false;
        this.lastFrameTime = 0; // Reset to avoid large delta
        this.log('Resumed');
        this.animationFrameId = requestAnimationFrame(this.animate);
    }

    destroy(): void {
        this.running = false;
        this.paused = false;

        if (this.animationFrameId !== null) {
            cancelAnimationFrame(this.animationFrameId);
            this.animationFrameId = null;
        }

        if (this.currentEffect) {
            this.currentEffect.clear();
        }

        if (this.resizeObserver) {
            this.resizeObserver.disconnect();
        }

        if (this.canvas.parentElement) {
            this.canvas.parentElement.removeChild(this.canvas);
        }

        this.log('Destroyed');
    }

    setSeason(season: Season, config?: SeasonConfig): void {
        this.currentSeason = season;

        if (config) {
            this.config.seasonConfig[season] = config;
        }

        const wasRunning = this.running && !this.paused;

        if (wasRunning) {
            this.pause();
        }

        this.initializeEffect();

        if (wasRunning) {
            this.resume();
        }

        this.log(`Switched to ${season} season`);
    }

    updateConfig(config: Partial<SeasonalFXConfig>): void {
        Object.assign(this.config, config);

        if (config.maxFPS !== undefined) {
            this.fpsThrottler.setMaxFPS(config.maxFPS);
        }

        if (config.season !== undefined) {
            this.setSeason(config.season);
        }

        if (config.particleCustomization !== undefined) {
            this.config.particleCustomization = {
                ...this.config.particleCustomization,
                ...config.particleCustomization,
            };
            if (this.currentEffect) {
                this.currentEffect.updateCustomization(this.config.particleCustomization);
                // Reinitialize to apply changes
                this.currentEffect.init(this.canvas.width, this.canvas.height);
            }
        }

        this.log('Configuration updated');
    }

    /**
     * Update particle customization options
     */
    updateParticleCustomization(customization: Partial<ParticleCustomization>): void {
        this.updateConfig({ particleCustomization: customization });
    }

    getMetrics(): PerformanceMetrics {
        return {
            fps: this.currentFPS,
            particleCount: this.currentEffect?.getParticles().length ?? 0,
            renderTime: this.renderTime,
        };
    }

    isRunning(): boolean {
        return this.running && !this.paused;
    }
}

/**
 * Factory function to create a new SeasonalFX instance
 */
export function createSeasonalFX(config: SeasonalFXConfig): ISeasonalFX {
    return new SeasonalFX(config);
}
